// src/auth/sessionContext.js
//
// Ticket 12: session context that exposes the signed-in user, every
// org they're a member of, and the currently-active org. Replaces the
// phase-1 stub that hardcoded `legacy-default-org` everywhere.
//
// Shape:
//   {
//     loading: boolean,
//     user: FirebaseUser | null,
//     globalRole: 'superAdmin' | 'user' | null,
//     memberships: Array<{
//       membershipId, orgId, role, orgName, orgStatus,
//     }>,
//     activeOrg: { orgId, orgName, role } | null,
//     setActiveOrg(orgId): void,
//   }
//
// activeOrg is the single source of truth for every query in the app
// that needs an orgId. Persisted in localStorage under `activeOrgId`.
// On load:
//   1. If stored id matches an approved membership → use it
//   2. Else first approved membership (sorted by orgName)
//   3. Else null (user lands on the no-access screen)

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { loadStudiesForOrg } from '../data/studiesHelpers';
import { listPendingInvitesForEmail, acceptInvite } from '../data/invitesHelpers';
import { normalizeMembershipRole } from './membershipHelpers';
import { ensureSignupAccess } from './signupProvisioning';

const STORAGE_KEY = 'activeOrgId';
const STUDY_STORAGE_PREFIX = 'activeStudyId:';

const SessionContext = createContext({
  loading: true,
  user: null,
  globalRole: null,
  memberships: [],
  activeOrg: null,
  setActiveOrg: () => {},
});

function loadStoredOrgId() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    return null;
  }
}

function saveStoredOrgId(orgId) {
  try {
    if (orgId) localStorage.setItem(STORAGE_KEY, orgId);
    else localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    // localStorage not available (e.g. private browsing) — no-op.
  }
}

function loadStoredStudyId(orgId) {
  if (!orgId) return null;
  try {
    return localStorage.getItem(STUDY_STORAGE_PREFIX + orgId);
  } catch (err) {
    return null;
  }
}

function saveStoredStudyId(orgId, studyId) {
  if (!orgId) return;
  try {
    if (studyId) localStorage.setItem(STUDY_STORAGE_PREFIX + orgId, studyId);
    else localStorage.removeItem(STUDY_STORAGE_PREFIX + orgId);
  } catch (err) {
    // ignore
  }
}

export function SessionProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [globalRole, setGlobalRole] = useState(null);
  const [membershipDocs, setMembershipDocs] = useState([]);
  const [membershipsLoaded, setMembershipsLoaded] = useState(false);
  const [orgDocsById, setOrgDocsById] = useState({});
  // manualOrgId is the user's explicit selection, persisted in
  // localStorage. If it matches an approved membership, that's the
  // active org. Otherwise activeOrg falls back to the first approved
  // membership. Only changes via setActiveOrg (the public setter).
  const [manualOrgId, setManualOrgIdState] = useState(() => loadStoredOrgId());
  const orphanRecoveryAttemptedRef = useRef(null);

  // 1. Firebase Auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      setUser(fbUser);
      if (!fbUser) {
        setGlobalRole(null);
        setMembershipDocs([]);
        setMembershipsLoaded(false);
        setOrgDocsById({});
        orphanRecoveryAttemptedRef.current = null;
        setLoading(false);
      } else {
        setMembershipDocs([]);
        setMembershipsLoaded(false);
        setOrgDocsById({});
        orphanRecoveryAttemptedRef.current = null;
        setLoading(true);
      }
    });
    return () => unsub();
  }, []);

  // 2. users/{uid}.globalRole — live-subscribed so the set-super-admin
  //    script reflection lands immediately.
  useEffect(() => {
    if (!user) return undefined;
    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setGlobalRole(data.globalRole || 'user');
    });
    return () => unsub();
  }, [user]);

  // 3. Memberships for the signed-in user — live-subscribed so
  //    accepting an invite (ticket 16) shows up without a reload.
  useEffect(() => {
    if (!user) return undefined;
    const q = query(
      collection(db, 'memberships'),
      where('uid', '==', user.uid),
    );
    const unsub = onSnapshot(
      q,
      async (snap) => {
        const docs = [];
        snap.forEach((d) => {
          docs.push({ membershipId: d.id, ...d.data() });
        });
        setMembershipDocs(docs);
        setMembershipsLoaded(true);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[session] memberships subscription:', err);
        setMembershipDocs([]);
        setMembershipsLoaded(true);
      },
    );
    return () => unsub();
  }, [user]);

  // 4. For each membership we don't already have an org doc for, fetch
  //    the org's name + status in a one-shot read. We don't need live
  //    updates on every org; membership-level live sub is enough to
  //    trigger a re-fetch when a new membership appears.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!membershipsLoaded) return;
      const needed = membershipDocs
        .map((m) => m.orgId)
        .filter((orgId) => orgId && !orgDocsById[orgId]);
      if (needed.length === 0) {
        setLoading(false);
        return;
      }
      const fetched = { ...orgDocsById };
      for (const orgId of needed) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const snap = await getDoc(doc(db, 'organizations', orgId));
          fetched[orgId] = snap.exists() ? snap.data() : null;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[session] could not fetch organizations/${orgId}:`, err);
          fetched[orgId] = null;
        }
      }
      if (!cancelled) {
        setOrgDocsById(fetched);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // orgDocsById intentionally omitted to avoid infinite re-fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipDocs, membershipsLoaded, user]);

  // Derived: the enriched memberships list. Only includes memberships
  // whose org doc has been fetched (drops any in-flight).
  const memberships = useMemo(() => {
    return membershipDocs
      .filter((m) => orgDocsById[m.orgId] !== undefined)
      .map((m) => {
        const org = orgDocsById[m.orgId];
        return {
          membershipId: m.membershipId,
          orgId: m.orgId,
          role: normalizeMembershipRole(m.role),
          orgName: org?.name || '(unnamed org)',
          orgStatus: org?.status || 'unknown',
        };
      })
      .sort((a, b) => (a.orgName || '').localeCompare(b.orgName || ''));
  }, [membershipDocs, orgDocsById]);

  const approvedMemberships = useMemo(
    () => memberships.filter((m) => m.orgStatus === 'approved'),
    [memberships],
  );

  // Pick the active org from the manual selection, falling back to the
  // first approved membership. Pure function of its inputs — no side
  // effects, no feedback loops. A manualOrgId that no longer matches
  // any approved membership is silently ignored until the user picks
  // again.
  const activeOrg = useMemo(() => {
    if (approvedMemberships.length === 0) return null;
    const stored = approvedMemberships.find((m) => m.orgId === manualOrgId);
    const target = stored || approvedMemberships[0];
    return {
      orgId: target.orgId,
      orgName: target.orgName,
      role: target.role,
    };
  }, [approvedMemberships, manualOrgId]);

  // Public setter. The only path that mutates localStorage /
  // manualOrgId. Called from the org switcher in AdminLayout.
  const setActiveOrg = (orgId) => {
    saveStoredOrgId(orgId);
    setManualOrgIdState(orgId);
  };

  // -------- Studies (ticket 13) --------
  //
  // For the active org, load the list of non-archived studies and
  // resolve an active study the same way as activeOrg. Separate state
  // from orgs because studies are per-org and load lazily whenever
  // the active org changes.

  const [studiesByOrg, setStudiesByOrg] = useState({});
  const [manualStudyId, setManualStudyIdState] = useState(null);
  const [studiesLoading, setStudiesLoading] = useState(false);

  useEffect(() => {
    if (!activeOrg) return undefined;
    let cancelled = false;
    setStudiesLoading(true);
    (async () => {
      try {
        const studies = await loadStudiesForOrg(activeOrg.orgId);
        if (cancelled) return;
        setStudiesByOrg((prev) => ({ ...prev, [activeOrg.orgId]: studies }));
        const stored = loadStoredStudyId(activeOrg.orgId);
        setManualStudyIdState(stored);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[session] loadStudiesForOrg:', err);
        if (!cancelled) {
          setStudiesByOrg((prev) => ({ ...prev, [activeOrg.orgId]: [] }));
        }
      } finally {
        if (!cancelled) setStudiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrg]);

  const studies = useMemo(() => {
    if (!activeOrg) return [];
    return studiesByOrg[activeOrg.orgId] || [];
  }, [studiesByOrg, activeOrg]);

  const activeStudy = useMemo(() => {
    if (!activeOrg || studies.length === 0) return null;
    const stored = studies.find((s) => s.id === manualStudyId);
    const target = stored || studies[0];
    return {
      studyId: target.id,
      studyName: target.name,
      orgId: target.orgId,
    };
  }, [activeOrg, studies, manualStudyId]);

  const setActiveStudy = (studyId) => {
    if (!activeOrg) return;
    saveStoredStudyId(activeOrg.orgId, studyId);
    setManualStudyIdState(studyId);
  };

  // Give AdminConfigPage / StudiesPage a way to poke the session
  // after creating a new study so the list refreshes without a reload.
  const reloadStudiesForActiveOrg = async () => {
    if (!activeOrg) return;
    const studies = await loadStudiesForOrg(activeOrg.orgId);
    setStudiesByOrg((prev) => ({ ...prev, [activeOrg.orgId]: studies }));
  };

  // -------- Pending invites (ticket 16) --------
  //
  // On login, query `invites` where email matches the authenticated
  // user and status == 'pending'. Surface the list to consumers so
  // they can show an accept/decline prompt.

  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingInvitesLoaded, setPendingInvitesLoaded] = useState(false);

  const reloadPendingInvites = async () => {
    if (!user || !user.email) {
      setPendingInvites([]);
      setPendingInvitesLoaded(true);
      return;
    }
    try {
      const list = await listPendingInvitesForEmail(user.email);
      setPendingInvites(list);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[session] listPendingInvitesForEmail:', err);
      setPendingInvites([]);
    } finally {
      setPendingInvitesLoaded(true);
    }
  };

  useEffect(() => {
    if (!user) {
      setPendingInvites([]);
      setPendingInvitesLoaded(false);
      return;
    }
    setPendingInvitesLoaded(false);
    reloadPendingInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user || !user.email) return;
    if (!membershipsLoaded || !pendingInvitesLoaded) return;
    if (globalRole == null || globalRole === 'superAdmin') return;
    if (membershipDocs.length > 0 || pendingInvites.length > 0) return;
    if (orphanRecoveryAttemptedRef.current === user.uid) return;

    orphanRecoveryAttemptedRef.current = user.uid;
    setLoading(true);

    (async () => {
      try {
        await ensureSignupAccess({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[session] ensureSignupAccess:', err);
        setLoading(false);
      }
    })();
  }, [
    globalRole,
    membershipDocs.length,
    membershipsLoaded,
    pendingInvites.length,
    pendingInvitesLoaded,
    user,
  ]);

  const acceptPendingInvite = async (invite) => {
    if (!user) throw new Error('no user');
    await acceptInvite(invite, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || '',
    });
    await reloadPendingInvites();
  };

  const value = useMemo(
    () => ({
      loading,
      user,
      globalRole,
      memberships,
      activeOrg,
      setActiveOrg,
      studies,
      studiesLoading,
      activeStudy,
      setActiveStudy,
      reloadStudiesForActiveOrg,
      pendingInvites,
      acceptPendingInvite,
      reloadPendingInvites,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, user, globalRole, memberships, activeOrg, studies, studiesLoading, activeStudy, pendingInvites],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession() {
  return useContext(SessionContext);
}

/** Shortcut for call-sites that just want { orgId, orgName, role }. */
export function useActiveOrgFromSession() {
  const { activeOrg } = useSession();
  return activeOrg;
}
